import { useDismissableView } from '@platform/dismissals/useDismissableView';
import { MOMENTS_COACH_FAQS_URL } from '@shared/constants';
import { Box, Button, Dialog, Icon, IconButton, Typography } from '@shared/ui';
import type { FC } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';
import GradientActionButton from './GradientActionButton';

const membersWelcomeModalKey = 'ui.members.welcome-modal@v1';

export type MembersWelcomeModalProps = {
  isEmptyState?: boolean;
};

const membersWelcomeFeatures = [
  {
    label: 'Session Summary',
    description: 'Summarize a single coaching session with action items, quotes, and insights',
  },
  {
    label: 'Prep for Next Session',
    description: 'Get a high-level overview of last session’s topics and next steps',
  },
  {
    label: 'Coaching Reflection',
    description: 'Assess your performance for growth opportunities and practice areas',
  },
  {
    label: 'Coaching Arc Summary',
    description: 'See patterns, accomplishments, and challenges across multiple sessions',
  },
] as const;

const MembersWelcomeModal: FC<MembersWelcomeModalProps> = ({ isEmptyState = false }) => {
  const { isVisible, isLoading, dismiss } = useDismissableView({
    key: membersWelcomeModalKey,
    mode: 'one-time',
  });
  const [open, setOpen] = useState(false);
  const didUserCloseRef = useRef(false);
  const prevIsVisibleRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    if (isLoading) return;

    const prevIsVisible = prevIsVisibleRef.current;
    prevIsVisibleRef.current = isVisible;

    if (!isVisible) {
      didUserCloseRef.current = false;
      setOpen(false);
      return;
    }

    if (didUserCloseRef.current) return;
    if (prevIsVisible !== true) setOpen(true);
  }, [isLoading, isVisible]);

  const handleClose = () => {
    didUserCloseRef.current = true;
    setOpen(false);
    void dismiss().catch(() => {}); // best-effort
  };

  const emptyStateDescription = `We noticed that you don’t have any transcribed sessions which means we can’t generate summaries, insights, or give coaching feedback. Start using Moments after transcribing your next session.`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      aria-labelledby="members-welcome-modal-title"
      BackdropProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.3)',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: '15px',
          height: isEmptyState ? '341px' : '416px',
          width: '670px',
        },
      }}
    >
      <Box sx={{ height: '100%', position: 'relative', p: '50px', textAlign: 'center' }}>
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          sx={{ position: 'absolute', left: '18px', top: '18px' }}
        >
          <Icon>close</Icon>
        </IconButton>

        {isEmptyState ? (
          <>
            <Typography
              id="members-welcome-modal-title"
              sx={{
                fontFamily: 'Basis Grotesque',
                fontSize: '24px',
                fontWeight: 700,
                lineHeight: '32px',
              }}
            >
              Welcome to Moments!
            </Typography>

            <Typography
              sx={{
                mt: '10px',
                fontSize: '20px',
                lineHeight: '32px',
              }}
            >
              {emptyStateDescription}
            </Typography>

            <Box
              sx={{
                mt: '24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '7px',
              }}
            >
              <GradientActionButton
                onClick={handleClose}
                href={MOMENTS_COACH_FAQS_URL}
                target="_blank"
                rel="noreferrer"
                sx={{ borderRadius: '12px', height: '42px', width: '301px' }}
              >
                Learn more about Moments
              </GradientActionButton>
            </Box>
          </>
        ) : (
          <>
            <Typography
              id="members-welcome-modal-title"
              sx={{
                fontFamily: 'Basis Grotesque',
                fontSize: '24px',
                fontWeight: 700,
                lineHeight: 1.334,
                width: '570px',
                mx: 'auto',
              }}
            >
              Welcome to Moments. Your new coaching tool.
            </Typography>

            <Typography
              sx={{
                mt: '10px',
                fontFamily: 'Basis Grotesque',
                fontSize: '15px',
                fontWeight: 500,
                lineHeight: 1.6,
                letterSpacing: '0.15px',
                width: '570px',
                mx: 'auto',
              }}
            >
              Here’s a few things you’ll be able to do:
            </Typography>

            <Box
              sx={{
                mt: '10px',
                display: 'grid',
                gridTemplateColumns: 'max-content 1fr',
                columnGap: '10px',
                rowGap: '10px',
                height: '185px',
                width: '570px',
                mx: 'auto',
                alignContent: 'center',
                alignItems: 'center',
              }}
            >
              {membersWelcomeFeatures.map((feature) => (
                <Fragment key={feature.label}>
                  <GradientActionButton
                    disabled
                    startIcon={
                      <Icon
                        aria-hidden
                        sx={{ width: '16px', height: '18px', color: 'common.white' }}
                      >
                        magic_button
                      </Icon>
                    }
                    sx={{
                      borderRadius: '12px',
                      px: '16px',
                      py: '4px',
                      fontSize: '12px',
                      lineHeight: '22px',
                      letterSpacing: '0.46px',
                      whiteSpace: 'nowrap',
                      minWidth: 'unset',
                      flex: '0 0 auto',
                      justifySelf: 'end',
                      '& .MuiButton-startIcon': {
                        mr: '8px',
                      },
                    }}
                  >
                    {feature.label}
                  </GradientActionButton>

                  <Typography
                    sx={{
                      fontFamily: 'Basis Grotesque',
                      fontSize: '10px',
                      fontWeight: 500,
                      lineHeight: 1.6,
                      letterSpacing: '0.15px',
                      color: 'common.black',
                      textAlign: 'left',
                      flex: '1 1 0',
                      minWidth: 0,
                      justifySelf: 'start',
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Fragment>
              ))}
            </Box>

            <Box
              sx={{
                mt: '10px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '7px',
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleClose}
                sx={{
                  borderRadius: '12px',
                  height: '42px',
                  width: '301px',
                  textTransform: 'none',
                  fontFamily: 'Basis Grotesque',
                  fontWeight: 700,
                  fontSize: '15px',
                  lineHeight: '26px',
                  letterSpacing: '0.4px',
                }}
              >
                Start By Generating a Summary
              </Button>

              <Button
                href={MOMENTS_COACH_FAQS_URL}
                target="_blank"
                rel="noreferrer"
                variant="text"
                sx={{
                  borderRadius: '12px',
                  height: '42px',
                  width: '182px',
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                  color: 'cement.500',
                  textTransform: 'none',
                  fontFamily: 'Basis Grotesque',
                  fontWeight: 500,
                  fontSize: '15px',
                  lineHeight: '26px',
                  letterSpacing: '0.4px',
                }}
              >
                Read FAQs
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
};

MembersWelcomeModal.displayName = 'MembersWelcomeModal';

export default MembersWelcomeModal;
